import { useState } from "react";
import { useParams } from "react-router-dom";
import VolunteerHeader from "../components/VolunteerHeader";
import MinistryIcon from "../components/MinistryIcon";
import Spinner from "../components/Spinner";
import PoweredByFooter from "../components/PoweredByFooter";
import { useVolunteerSession } from "../lib/volunteerSession";
import { useVolunteerDashboard } from "../lib/useVolunteerDashboard";
import { formatMassDateLabel, formatMassTimeParts } from "../lib/mass-time";
import { roleLabel } from "../lib/role-label";
import { resolveMspLoginUrl } from "../lib/msp-links";
import type { CoServer, OpenSubRequest, VolunteerMassItem } from "../types/volunteer-portal";
import { payloadTimeZone } from "../lib/timezone";
import { reportVolunteerEvent } from "../lib/volunteerEvents";

// The volunteer's home screen — every Mass they're scheduled for
// (soonest first) and, per Mass, who else is serving alongside them.
// Read-only: everything here comes from GET /api/volunteer/dashboard,
// which itself only ever reads MSP. There is no editing surface in
// this sprint (availability/preference writes are the next feature,
// blocked on MSP write access — see design/architecture.md).
export default function VolunteerDashboard() {
  const { parish: slug = "" } = useParams();
  const session = useVolunteerSession();
  const { data, error, loading } = useVolunteerDashboard();

  const loginPath = `/${slug}/login`;

  // Mobile-only tabs (CSS decides whether to honor `tab` — see
  // .volunteer-dashboard__panel). Under 720px the two columns stack,
  // so My Masses and the sub-requests read as one long scroll; the
  // tabs let a volunteer jump straight to either. Desktop keeps both
  // side by side and hides the tabs. Same pattern as the public
  // schedule's Weekend/Daily tabs.
  const [tab, setTab] = useState<"masses" | "subs">("masses");

  const firstName = (data?.volunteerName?.trim() || session.displayName).split(" ")[0];
  const greeting = (
    <h1 className="dashboard__greeting-title">{firstName ? `Hi, ${firstName}` : "My schedule"}</h1>
  );

  return (
    <main className="page page--dashboard">
      <VolunteerHeader
        loginPath={loginPath}
        greeting={greeting}
        scheduleUrl={data?.publicScheduleSlug ? `/s/${data.publicScheduleSlug}` : undefined}
      />

      {loading ? (
        <Spinner label="Loading your schedule" />
      ) : error || !data ? (
        <p className="placeholder">Couldn't load your schedule{error ? `: ${error}` : ""}.</p>
      ) : data.status === "pending" ? (
        <section className="panel">
          <p className="panel__empty">{data.message}</p>
        </section>
      ) : (
        <>
          <div className="volunteer-dashboard__tabs" role="tablist" aria-label="Dashboard sections">
            <button
              type="button"
              role="tab"
              id="vtab-masses"
              aria-selected={tab === "masses"}
              aria-controls="vpanel-masses"
              className={`volunteer-dashboard__tab${tab === "masses" ? " is-active" : ""}`}
              onClick={() => setTab("masses")}
            >
              My Masses
              <span className="volunteer-dashboard__tab-count">{data.masses.length}</span>
            </button>
            <button
              type="button"
              role="tab"
              id="vtab-subs"
              aria-selected={tab === "subs"}
              aria-controls="vpanel-subs"
              className={`volunteer-dashboard__tab${tab === "subs" ? " is-active" : ""}`}
              onClick={() => setTab("subs")}
            >
              Open sub-requests
              <span className="volunteer-dashboard__tab-count">{data.openSubRequests.length}</span>
            </button>
          </div>

          <div className="volunteer-dashboard__cols">
            <div
              id="vpanel-masses"
              role="tabpanel"
              aria-labelledby="vtab-masses"
              className={`volunteer-dashboard__panel${tab === "masses" ? "" : " is-inactive"}`}
            >
              <div className="dashboard__section">
                <h2 id="my-masses-title" className="dashboard__section-label">
                  My Masses
                </h2>
                {data.masses.length === 0 ? (
                  <section className="panel" aria-labelledby="my-masses-title">
                    <p className="panel__empty">No upcoming Masses on your schedule right now.</p>
                  </section>
                ) : (
                  <ul className="volunteer-masses__list" aria-labelledby="my-masses-title">
                    {data.masses.map((m) => (
                      <VolunteerMassCard
                        key={m.massId}
                        mass={m}
                        timeZone={payloadTimeZone(data.timezone)}
                        mspLoginUrl={resolveMspLoginUrl(data.mspLoginUrl)}
                      />
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div
              id="vpanel-subs"
              role="tabpanel"
              aria-labelledby="vtab-subs"
              className={`volunteer-dashboard__panel${tab === "subs" ? "" : " is-inactive"}`}
            >
              <OpenSubRequests
                subRequests={data.openSubRequests}
                timeZone={payloadTimeZone(data.timezone)}
                mspLoginUrl={resolveMspLoginUrl(data.mspLoginUrl)}
              />
            </div>
          </div>
        </>
      )}

      <PoweredByFooter />
    </main>
  );
}

// "Open sub-requests" — open sub-requests the volunteer is qualified
// to cover (worker filters to their ministries). On desktop this sits
// in the right column beside My Masses (.volunteer-dashboard__cols);
// on mobile it stacks below. Read-only by design: MSP is read-only
// across this product, so there's no claim button — the pane surfaces
// the need and points at the coordinator, leaving a clean seam for a
// real claim flow once MSP write access lands. Shown on every linked
// dashboard (with a reassuring empty state) so it's a dependable place
// to check, not something that only appears mid-crisis.
function OpenSubRequests({
  subRequests,
  timeZone,
  mspLoginUrl,
}: {
  subRequests: OpenSubRequest[];
  timeZone: string;
  mspLoginUrl: string;
}) {
  return (
    <div className="dashboard__section">
      <h2 id="open-subs-title" className="dashboard__section-label">
        Open sub-requests
      </h2>
      {subRequests.length === 0 ? (
        <section className="panel" aria-labelledby="open-subs-title">
          <p className="panel__empty">No open sub-requests for your ministries right now.</p>
        </section>
      ) : (
        <>
          <p className="dashboard__section-hint">
            See one you can cover? Pick it up in{" "}
            <a
              className="dashboard__section-hint-link"
              href={mspLoginUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => void reportVolunteerEvent("sub_requested")}
            >
              Ministry Scheduler Pro →
            </a>
          </p>
          <ul className="coverage-list" aria-labelledby="open-subs-title">
            {subRequests.map((sr) => (
              <SubRequestItem key={sr.id} sr={sr} timeZone={timeZone} />
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// The Mass date/time leads the card — a sub-request is fundamentally
// "this specific Mass needs someone," so the when reads first and
// largest (same treatment as the My Masses cards). Then the role and
// who submitted it: e.g. Jane Smith, the Communion Minister who can't
// make it, is who you'd be "covering for." The MSP position detail
// (e.g. "Station 3") is intentionally dropped here — noise for a
// volunteer deciding whether to help.
function SubRequestItem({ sr, timeZone }: { sr: OpenSubRequest; timeZone: string }) {
  const d = new Date(sr.startsAt);
  const dateLabel = formatMassDateLabel(d, timeZone);
  const { time, suffix } = formatMassTimeParts(d, timeZone);
  const role = roleLabel(sr.ministry);
  return (
    <li className="coverage-item">
      <p className="coverage-item__date">{dateLabel}</p>
      <p className="coverage-item__time">
        {time}
        <span className="coverage-item__time-suffix">{suffix}</span>
      </p>
      <p className="coverage-item__role">
        <MinistryIcon ministry={role} />
        <span>{role}</span>
      </p>
      <p className="coverage-item__for">Covering for {sr.requestedByName}</p>
    </li>
  );
}

function VolunteerMassCard({
  mass,
  timeZone,
  mspLoginUrl,
}: {
  mass: VolunteerMassItem;
  timeZone: string;
  mspLoginUrl: string;
}) {
  const d = new Date(mass.startsAt);
  const dateLabel = formatMassDateLabel(d, timeZone);
  const { time, suffix } = formatMassTimeParts(d, timeZone);
  const myRole = roleLabel(mass.myRole.ministry);
  const myRoleText =
    mass.myRole.position && mass.myRole.position !== mass.myRole.ministry
      ? `${myRole} — ${mass.myRole.position}`
      : myRole;

  return (
    <li className="volunteer-mass">
      <div className="volunteer-mass__head">
        <div className="volunteer-mass__when">
          <p className="volunteer-mass__date">{dateLabel}</p>
          <p className="volunteer-mass__time">
            {time}
            <span className="volunteer-mass__time-suffix">{suffix}</span>
          </p>
        </div>
        <span className="volunteer-mass__role">
          <MinistryIcon ministry={myRole} />
          <span>{myRoleText}</span>
        </span>
      </div>

      <div className="volunteer-mass__coservers">
        <p className="volunteer-mass__coservers-title">Serving with you</p>
        {mass.coServers.length === 0 ? (
          <p className="mass-roster__empty">No one else scheduled yet.</p>
        ) : (
          <ul className="mass-roster__list">
            {mass.coServers.map((c) => (
              <CoServerRow key={`${c.volunteerId}-${c.ministry}-${c.position}`} c={c} />
            ))}
          </ul>
        )}
      </div>

      {/* The door out, exactly where the need arises: a volunteer
          who can't make THIS Mass is looking at THIS card. Mirrors
          the emailed schedule page's per-Mass link. Sub requests are
          made in MSP (we're read-only); the link is per-parish now
          (payload mspLoginUrl via resolveMspLoginUrl). */}
      <p className="volunteer-mass__sub-link">
        Can't make it?{" "}
        <a
          href={mspLoginUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => void reportVolunteerEvent("sub_requested")}
        >
          Request a sub →
        </a>
      </p>
    </li>
  );
}

// Name + role only — never email/phone. The worker's payload already
// omits contact info entirely (see worker/src/routes/volunteer-portal.ts),
// so there's nothing here to accidentally leak; this mirrors the
// admin Mass modal's roster row exactly (MassModal.tsx's
// MassRosterRow) so the two surfaces read as the same component.
function CoServerRow({ c }: { c: CoServer }) {
  const role = roleLabel(c.ministry);
  return (
    <li className="mass-roster__row">
      <span className="mass-roster__role">
        <MinistryIcon ministry={role} />
        <span>{c.position && c.position !== c.ministry ? `${role} — ${c.position}` : role}</span>
      </span>
      <span className="mass-roster__sep" aria-hidden="true">
        ·
      </span>
      <span className="mass-roster__name">{c.name}</span>
    </li>
  );
}
